import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Bell, BellOff, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { requestNotificationPermission, areNotificationsEnabled } from '@/services/notifications/oneSignalService';

interface NotificationSettingsProps {
  pushNotifications?: boolean;
  pushMessages?: boolean;
  pushSwapOffers?: boolean;
  pushSwapResponses?: boolean;
  pushWeeklyDigest?: boolean;
  pushNewReagents?: boolean;
  pushReagentRequests?: boolean;
  onUpdate: (settings: {
    push_notifications?: boolean;
    push_messages?: boolean;
    push_swap_offers?: boolean;
    push_swap_responses?: boolean;
    push_weekly_digest?: boolean;
    push_new_reagents?: boolean;
    push_reagent_requests?: boolean;
  }) => void;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  pushNotifications = true,
  pushMessages = true,
  pushSwapOffers = true,
  pushSwapResponses = true,
  pushWeeklyDigest = true,
  pushNewReagents = true,
  pushReagentRequests = true,
  onUpdate,
}) => {
  const { toast } = useToast();
  const [isRequesting, setIsRequesting] = useState(false);
  const browserNotificationsEnabled = areNotificationsEnabled();

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      const granted = await requestNotificationPermission();
      if (granted) {
        toast({
          title: "Notifications enabled",
          description: "You'll now receive push notifications for important updates.",
        });
        onUpdate({ push_notifications: true });
      } else {
        toast({
          title: "Permission denied",
          description: "You can enable notifications in your browser settings.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to request notification permission.",
        variant: "destructive",
      });
    } finally {
      setIsRequesting(false);
    }
  };

  const handleToggle = (key: string, value: boolean) => {
    onUpdate({ [key]: value });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Push Notifications
        </CardTitle>
        <CardDescription>
          Configure when you want to receive push notifications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!browserNotificationsEnabled && (
          <div className="p-4 bg-muted rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <BellOff className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm font-medium">Enable Browser Notifications</p>
                <p className="text-xs text-muted-foreground">
                  Allow notifications to receive real-time updates
                </p>
              </div>
              <Button 
                onClick={handleRequestPermission}
                disabled={isRequesting}
                size="sm"
              >
                {isRequesting ? 'Enabling...' : 'Enable'}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="push-notifications" className="text-sm font-medium">
                Push Notifications
              </Label>
              <p className="text-xs text-muted-foreground">
                Master switch for all push notifications
              </p>
            </div>
            <Switch
              id="push-notifications"
              checked={pushNotifications}
              onCheckedChange={(checked) => handleToggle('push_notifications', checked)}
            />
          </div>

          {pushNotifications && (
            <>
              <div className="flex items-center justify-between pl-4">
                <div className="space-y-1">
                  <Label htmlFor="push-messages" className="text-sm">
                    New Messages
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When someone sends you a message
                  </p>
                </div>
                <Switch
                  id="push-messages"
                  checked={pushMessages}
                  onCheckedChange={(checked) => handleToggle('push_messages', checked)}
                />
              </div>

              <div className="flex items-center justify-between pl-4">
                <div className="space-y-1">
                  <Label htmlFor="push-swap-offers" className="text-sm">
                    Swap Requests
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When someone wants to swap for your reagent
                  </p>
                </div>
                <Switch
                  id="push-swap-offers"
                  checked={pushSwapOffers}
                  onCheckedChange={(checked) => handleToggle('push_swap_offers', checked)}
                />
              </div>

              <div className="flex items-center justify-between pl-4">
                <div className="space-y-1">
                  <Label htmlFor="push-swap-responses" className="text-sm">
                    Swap Responses
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When your swap requests are accepted or rejected
                  </p>
                </div>
                <Switch
                  id="push-swap-responses"
                  checked={pushSwapResponses}
                  onCheckedChange={(checked) => handleToggle('push_swap_responses', checked)}
                />
              </div>

              <div className="flex items-center justify-between pl-4">
                <div className="space-y-1">
                  <Label htmlFor="push-weekly-digest" className="text-sm">
                    Weekly Digest
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Weekly summary of new reagents and activity
                  </p>
                </div>
                <Switch
                  id="push-weekly-digest"
                  checked={pushWeeklyDigest}
                  onCheckedChange={(checked) => handleToggle('push_weekly_digest', checked)}
                />
              </div>

              <div className="flex items-center justify-between pl-4">
                <div className="space-y-1">
                  <Label htmlFor="push-new-reagents" className="text-sm">
                    New Reagents
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When new reagents are uploaded to the platform
                  </p>
                </div>
                <Switch
                  id="push-new-reagents"
                  checked={pushNewReagents}
                  onCheckedChange={(checked) => handleToggle('push_new_reagents', checked)}
                />
              </div>

              <div className="flex items-center justify-between pl-4">
                <div className="space-y-1">
                  <Label htmlFor="push-reagent-requests" className="text-sm">
                    Reagent Requests
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When someone requests reagents you might have
                  </p>
                </div>
                <Switch
                  id="push-reagent-requests"
                  checked={pushReagentRequests}
                  onCheckedChange={(checked) => handleToggle('push_reagent_requests', checked)}
                />
              </div>
            </>
          )}
        </div>

        {browserNotificationsEnabled && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Bell className="h-4 w-4" />
            Browser notifications are enabled
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;